import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LogOut, FileText, X } from "lucide-react";
import SEO from "../components/SEO";
import Logo from "../components/Logo";

export default function AdminDashboard() {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) {
      nav("/admin/login");
      return;
    }
    api
      .get("/inquiry")
      .then((r) => setInquiries(r.data))
      .finally(() => setFetching(false));
  }, [user, loading, nav]);

  const onLogout = () => {
    logout();
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-bg">
      <SEO title="Admin | B&S Global Ventures" robots="noindex, nofollow" />
      <header className="border-b border-border bg-surface">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="h-10 md:h-11 w-auto" />
            <span className="label-cap text-accent">Admin</span>
          </div>
          <button type="button" onClick={onLogout} className="btn-outline !py-2 !px-4 text-sm">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="label-cap text-accent mb-2">Inquiry Inbox</div>
            <h1 className="font-serif text-4xl font-bold text-primary">Submissions</h1>
          </div>
          <div className="label-cap text-muted">{inquiries.length} total</div>
        </div>

        {fetching ? (
          <p className="text-muted">Loading…</p>
        ) : inquiries.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <FileText className="mx-auto mb-4 text-muted" size={28} />
            <p className="font-medium text-primary">No inquiries yet</p>
          </div>
        ) : (
          <div className="glass-card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left label-cap text-muted border-b border-border">
                  <th className="px-6 py-4">Reference</th>
                  <th className="px-6 py-4">Company</th>
                  <th className="px-6 py-4">Country</th>
                  <th className="px-6 py-4">Direction</th>
                  <th className="px-6 py-4">Contact</th>
                  <th className="px-6 py-4">Received</th>
                </tr>
              </thead>
              <tbody>
                {inquiries.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => setSelected(r)}
                    className="border-b border-border last:border-b-0 cursor-pointer hover:bg-surface-alt/50 transition-colors"
                  >
                    <td className="px-6 py-4 font-mono text-accent">{r.reference}</td>
                    <td className="px-6 py-4 text-primary">{r.company_name}</td>
                    <td className="px-6 py-4 text-muted">{r.country}</td>
                    <td className="px-6 py-4 text-muted">{r.trade_direction}</td>
                    <td className="px-6 py-4 text-muted">{r.contact_name}</td>
                    <td className="px-6 py-4 text-muted">
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-primary/40 backdrop-blur-sm">
          <div className="bg-surface rounded-2xl border border-border max-w-lg w-full max-h-[85vh] overflow-y-auto p-8 relative">
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="absolute top-4 right-4 text-muted hover:text-primary"
            >
              <X size={20} />
            </button>
            <div className="label-cap text-accent mb-2">{selected.reference}</div>
            <h2 className="font-serif text-2xl font-bold text-primary">{selected.company_name}</h2>
            <dl className="mt-6 space-y-3 text-sm">
              {[
                ["Country", selected.country],
                ["Business Type", selected.business_type],
                ["Trade Direction", selected.trade_direction],
                ["Volume", selected.purchase_volume],
                ["Categories", (selected.categories || []).join(", ")],
                ["Private Label", selected.private_label ? "Yes" : "No"],
                ["Requirements", selected.requirements || "—"],
                ["Contact", selected.contact_name],
                ["Email", selected.email],
                ["Phone", selected.phone || "—"],
                ["Preferred Contact", selected.preferred_contact],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-4">
                  <dt className="text-muted w-36 shrink-0">{k}</dt>
                  <dd className="text-primary">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}
